import { useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
    AppBar, Avatar, Box, Button, Container, Divider, Drawer, IconButton, List, ListItemButton,
    ListItemIcon, Menu, MenuItem, Toolbar, Typography, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    IconMenu2, IconLogout, IconX, IconLayoutDashboard, IconAlertCircle, IconUser, IconLock
} from '@tabler/icons';
import { THEME_COLOR_DARK } from 'utils/Constants';
import { formatMobileDisplay } from 'utils/Functions';
import jwt from 'jwtservice/jwtService';

const NAV_ITEMS = [
    { label: 'Dashboard',  path: '/user/dashboard',  Icon: IconLayoutDashboard },
    { label: 'Complaints', path: '/user/complaints', Icon: IconAlertCircle },
];

const ACCOUNT_ITEMS = [
    { label: 'My Profile',      path: '/user/profile',         Icon: IconUser },
    { label: 'Change Password', path: '/user/change-password', Icon: IconLock },
];

export default function UserLayout() {
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [userInfo, setUserInfoState] = useState(() => jwt.getUserInfo());
    const [tenant, setTenant] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState(null);

    useEffect(() => {
        const refresh = () => setUserInfoState(jwt.getUserInfo());
        window.addEventListener('userinfo-updated', refresh);
        window.addEventListener('storage', refresh);
        return () => {
            window.removeEventListener('userinfo-updated', refresh);
            window.removeEventListener('storage', refresh);
        };
    }, []);

    useEffect(() => {
        jwt.getMyTenant()
            .then((res) => setTenant(res.data || null))
            .catch(() => { /* ignore — fall back to default branding */ });
    }, []);

    useEffect(() => {
        jwt.getMyProfile()
            .then((res) => {
                const u = res.data || {};
                const stamped = u.profileImage
                    ? `${u.profileImage}${u.profileImage.includes('?') ? '&' : '?'}t=${Date.now()}`
                    : '';
                const merged = {
                    ...(jwt.getUserInfo() || {}),
                    fullname: u.fullname,
                    userId: u.userId,
                    email: u.email,
                    mobile: u.mobile,
                    profileImage: stamped,
                };
                jwt.setUserInfo(merged);
                setUserInfoState(merged);
            })
            .catch(() => { /* ignore — keep cached userInfo */ });
    }, []);

    const handleLogout = () => {
        setMenuAnchor(null);
        jwt.clearUserSession();
        navigate('/user-login');
    };

    const goTo = (path) => {
        setDrawerOpen(false);
        setMenuAnchor(null);
        navigate(path);
    };

    const initials = useMemo(() => {
        const name = (userInfo?.fullname || userInfo?.userId || '').trim();
        if (!name) return '?';
        return name.split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('');
    }, [userInfo?.fullname, userInfo?.userId]);

    const avatarSrc = userInfo?.profileImage || undefined;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <AppBar position="static" elevation={0} sx={{ bgcolor: '#0f172a', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <Toolbar sx={{ px: { xs: 1.5, md: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mr: { xs: 1, md: 4 }, flexGrow: { xs: 1, md: 0 }, minWidth: 0 }} onClick={() => navigate('/user/dashboard')}>
                        <Box sx={{ height: 32, width: 32, borderRadius: '8px', bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 1.25, overflow: 'hidden', flexShrink: 0 }}>
                            <img src={tenant?.logo || '/logo-isp-daftar.png'} alt={tenant?.name || 'IspDaftar'}
                                style={{ maxHeight: 28, maxWidth: 28, objectFit: 'contain' }}
                                onError={(e) => { e.currentTarget.src = '/logo-isp-daftar.png'; }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ color: '#f1f5f9', fontWeight: 700, fontSize: { xs: 14, md: 16 }, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {tenant?.name || 'Customer Portal'}
                            </Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, lineHeight: 1.2, letterSpacing: 0.4, textTransform: 'uppercase', display: { xs: 'none', sm: 'block' } }}>
                                {tenant?.contactNumber ? `Support: ${formatMobileDisplay(tenant.contactNumber)}` : 'Customer Portal'}
                            </Typography>
                        </Box>
                    </Box>

                    {!isMobile && (
                        <>
                            <Box sx={{ display: 'flex', gap: 0.5, flexGrow: 1 }}>
                                {NAV_ITEMS.map(({ label, path, Icon }) => (
                                    <Button key={path} onClick={() => navigate(path)} size="small"
                                        startIcon={<Icon size={16} />}
                                        sx={{
                                            color: location.pathname === path ? THEME_COLOR_DARK : 'rgba(255,255,255,0.85)',
                                            textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 2,
                                            bgcolor: location.pathname === path ? `${THEME_COLOR_DARK}18` : 'transparent',
                                            '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' },
                                        }}>
                                        {label}
                                    </Button>
                                ))}
                            </Box>

                            <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ p: 0.5 }}>
                                <Avatar src={avatarSrc} sx={{ width: 36, height: 36, bgcolor: THEME_COLOR_DARK, fontSize: 14, fontWeight: 700 }}>
                                    {!avatarSrc && initials}
                                </Avatar>
                            </IconButton>
                            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                PaperProps={{ sx: { mt: 1, minWidth: 220, borderRadius: 2 } }}>
                                <Box sx={{ px: 2, py: 1.25 }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a', wordBreak: 'break-word' }}>
                                        {userInfo?.fullname || userInfo?.userId}
                                    </Typography>
                                    {userInfo?.email && (
                                        <Typography sx={{ fontSize: 12, color: '#64748b', wordBreak: 'break-word' }}>{userInfo.email}</Typography>
                                    )}
                                </Box>
                                <Divider />
                                {ACCOUNT_ITEMS.map(({ label, path, Icon }) => (
                                    <MenuItem key={path} onClick={() => goTo(path)}>
                                        <ListItemIcon><Icon size={18} /></ListItemIcon>
                                        <Typography sx={{ fontSize: 14 }}>{label}</Typography>
                                    </MenuItem>
                                ))}
                                <Divider />
                                <MenuItem onClick={handleLogout}>
                                    <ListItemIcon><IconLogout size={18} color="#ef4444" /></ListItemIcon>
                                    <Typography sx={{ fontSize: 14, color: '#ef4444', fontWeight: 600 }}>Logout</Typography>
                                </MenuItem>
                            </Menu>
                        </>
                    )}

                    {isMobile && (
                        <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: '#f1f5f9' }}>
                            <IconMenu2 size={22} />
                        </IconButton>
                    )}
                </Toolbar>
            </AppBar>

            <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}
                PaperProps={{ sx: { width: 280, bgcolor: '#0f172a', color: '#f1f5f9' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Menu</Typography>
                    <IconButton size="small" onClick={() => setDrawerOpen(false)} sx={{ color: '#f1f5f9' }}>
                        <IconX size={20} />
                    </IconButton>
                </Box>
                <Box sx={{ px: 2, pb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar src={avatarSrc} sx={{ width: 44, height: 44, bgcolor: THEME_COLOR_DARK, fontSize: 14, fontWeight: 700 }}>
                        {!avatarSrc && initials}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ color: '#f1f5f9', fontSize: 14, fontWeight: 700, wordBreak: 'break-word' }}>
                            {userInfo?.fullname || userInfo?.userId}
                        </Typography>
                        {userInfo?.email && (
                            <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, wordBreak: 'break-word' }}>{userInfo.email}</Typography>
                        )}
                    </Box>
                </Box>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
                <List sx={{ flex: '1 1 auto', p: 1 }}>
                    {[...NAV_ITEMS, ...ACCOUNT_ITEMS].map(({ label, path, Icon }) => {
                        const active = location.pathname === path;
                        return (
                            <ListItemButton key={path} onClick={() => goTo(path)}
                                sx={{
                                    borderRadius: '8px', mb: 0.5,
                                    color: '#ffffff !important',
                                    bgcolor: active ? `${THEME_COLOR_DARK}22` : 'transparent',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                                }}>
                                <ListItemIcon sx={{ minWidth: 36, color: '#ffffff' }}>
                                    <Icon size={18} />
                                </ListItemIcon>
                                <Typography sx={{ color: '#ffffff', fontWeight: active ? 700 : 600, fontSize: 14 }}>
                                    {label}
                                </Typography>
                            </ListItemButton>
                        );
                    })}
                </List>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
                <Box sx={{ p: 2 }}>
                    <Button fullWidth onClick={handleLogout} variant="outlined" startIcon={<IconLogout size={18} />}
                        sx={{ color: 'rgba(255,255,255,0.85)', borderColor: 'rgba(255,255,255,0.2)', textTransform: 'none', fontWeight: 600, borderRadius: '8px', '&:hover': { borderColor: 'rgba(255,255,255,0.5)', bgcolor: 'rgba(255,255,255,0.05)' } }}>
                        Logout
                    </Button>
                </Box>
            </Drawer>

            <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1.5, md: 3 } }}>
                <Outlet />
            </Container>
        </Box>
    );
}
